import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import mapStore from "@/entities/map/model/map.store"
import { Map as EstablishmentMap } from "@/entities/map/model/map.types"
import { Site } from "../model/site.types"
import SiteChip from "./SiteChip"
import siteStore from "../model/site.store"

interface Props {
  onReplace?: () => void
  showAdd?: boolean
  showRemove?: boolean
  selectionMode?: "multi" | "single"
  sites: Site[]
}

export default function SiteFilterChips({
  onReplace,
  showAdd = true,
  showRemove,
  selectionMode = "multi",
  sites
}: Props) {
  const dispatch = useAppDispatch()
  const maps = useAppSelector(mapStore.selectors.list)
  const selectedSiteIds = useAppSelector(siteStore.selectors.selected)
  const mapsById = new Map(maps.map((map) => [map.id, map]))
  const mapIds = Array.from(new Set(sites.map((site) => site.MapId).filter((id): id is string => Boolean(id))))

  function selectSite(site: Site) {
    if (selectionMode === "multi") {
      dispatch(siteStore.actions.select(site.id))
      return
    }

    if (selectedSiteIds.includes(site.id)) {
      dispatch(siteStore.actions.select([]))
      return
    }

    onReplace?.()
    dispatch(siteStore.actions.select([site.id]))
  }

  function addSite(site: Site) {
    if (!selectedSiteIds.includes(site.id)) {
      dispatch(siteStore.actions.select(selectedSiteIds.concat(site.id)))
    }
  }

  function removeSite(site: Site) {
    dispatch(siteStore.actions.select(selectedSiteIds.filter((id) => id !== site.id)))
  }

  const getAddClick = (site: Site) =>
    selectionMode === "single" && showAdd && !selectedSiteIds.includes(site.id) ? () => addSite(site) : undefined
  const getRemoveClick = (site: Site) =>
    selectionMode === "single" && showRemove && selectedSiteIds.includes(site.id) ? () => removeSite(site) : undefined

  if (mapIds.length <= 1) {
    return (
      <div className="mt-10 flex flex-wrap gap-[10px]">
        {sites.map((site) => (
          <SiteChip
            key={site.id}
            site={site}
            onAddClick={getAddClick(site)}
            onClick={() => selectSite(site)}
            onRemoveClick={getRemoveClick(site)}
          />
        ))}
      </div>
    )
  }

  const groups: { map: EstablishmentMap | null; sites: Site[] }[] = maps
    .filter((map) => mapIds.includes(map.id))
    .sort((a, b) => a.order - b.order)
    .map((map) => ({
      map,
      sites: sites.filter((site) => site.MapId === map.id)
    }))
  if (sites.some((site) => !site.MapId)) {
    groups.push({
      map: null,
      sites: sites.filter((site) => !site.MapId)
    })
  }

  return (
    <div className="mt-10">
      {groups.map((group) => (
        <div key={group.map?.id ?? "without-map"} className="mb-6 last:mb-0">
          <div className="grey-t text-sm font-bold mb-2">{group.map?.name ?? "Sans niveau"}</div>
          <div className="flex flex-wrap gap-[10px]">
            {group.sites.map((site) => (
              <SiteChip
                key={site.id}
                site={site}
                onAddClick={getAddClick(site)}
                onClick={() => selectSite(site)}
                onRemoveClick={getRemoveClick(site)}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-[10px]">
        {sites
          .filter((site) => site.MapId && !mapsById.has(site.MapId))
          .map((site) => (
            <SiteChip
              key={site.id}
              site={site}
              onAddClick={getAddClick(site)}
              onClick={() => selectSite(site)}
              onRemoveClick={getRemoveClick(site)}
            />
          ))}
      </div>
    </div>
  )
}
